import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";
import {
  ROOT_DOMAIN,
  normalizeSubdomain as normalizeSubdomainLabel,
} from "../services/inviteDomain";

const normalizeSubdomain = normalizeSubdomainLabel;

const isValidSubdomain = (value) =>
  /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(value);

const INVITE_CONFIG = {
  SUPERADMIN: {
    tabLabel: "Admin",
    targetRole: "ADMIN",
    action: "superadmin.createAdmin",
    needsSubdomain: true,
    submitLabel: "Send Admin Invite",
  },
  ADMIN: {
    tabLabel: "Owner",
    targetRole: "OWNER",
    action: "admin.createOwner",
    needsSubdomain: false,
    needsMaintenance: true,
    submitLabel: "Send Owner Invite",
  },
  OWNER: {
    tabLabel: "Tenant",
    targetRole: "TENANT",
    action: "owner.createTenant",
    needsSubdomain: false,
    submitLabel: "Send Tenant Invite",
  },
};

const normalizeStoredRole = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  subdomain: "",
  monthlyMaintenanceAmount: ""
};

const Invite = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const loggedInRole = normalizeStoredRole(localStorage.getItem("role"));
  const config = INVITE_CONFIG[loggedInRole] || null;

  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]); 
  const [loadingRoles, setLoadingRoles] = useState(false);
  const isOwnerInvite = loggedInRole === "OWNER";
  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantResults, setTenantResults] = useState([]);
  const [searchingTenant, setSearchingTenant] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  useEffect(() => {
    if (!isOwnerInvite || !API_BASE_URL || selectedTenant) return;

    const timer = setTimeout(async () => {
      setSearchingTenant(true);
      try {
        const response = await axios.post(`${API_BASE_URL}`, {
          action: "owner.searchTenant",
          userid: localStorage.getItem("userid"),
          search: tenantSearch.trim(),
        });
        const data = response?.data;
        if (data?.success === true || data?.status === "success") {
          setTenantResults(Array.isArray(data?.tenants) ? data.tenants : []);
        } else {
          setTenantResults([]);
          if (tenantSearch.trim()) toast.error(data?.message || "Search failed");
        }
      } catch (error) {
        setTenantResults([]);
      } finally {
        setSearchingTenant(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [tenantSearch, isOwnerInvite, API_BASE_URL, selectedTenant]);

  const pickTenant = (t) => {
    setSelectedTenant(t);
    setForm((f) => ({ ...f, name: t.name, email: t.email, phone: t.phone || "" }));
    setTenantResults([]);
  };

  const clearTenant = () => {
    setSelectedTenant(null);
    setTenantSearch("");
    setForm((f) => ({ ...f, name: "", email: "", phone: "" }));
  };

  const roleName = useMemo(() => {
    if (!config) return "";
    const found = roleOptions.find(
      (r) => String(r?.name || "").toUpperCase().trim() === config.targetRole
    );
    return found ? String(found.name).toUpperCase().trim() : "";
  }, [roleOptions, config]);

  useEffect(() => {
    const fetchRoles = async () => {
      if (!API_BASE_URL || !config) return;

      setLoadingRoles(true);
      let didIncrement = false;
      try {
        loaderIncrement();
        didIncrement = true;
        const response = await axios.post(`${API_BASE_URL}`, {
          action: "getRoles",
        });
        const data = response?.data;

        if (data?.status === "success") {
          setRoleOptions(Array.isArray(data?.data) ? data.data : []);
        } else {
          toast.error(data?.message || "Failed to load roles");
          setRoleOptions([]);
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to load roles"
        );
        setRoleOptions([]);
      } finally {
        if (didIncrement) loaderDecrement();
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, [API_BASE_URL, loggedInRole]);

  const onChange = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

    const resetForm = () => {
    setForm(emptyForm);
    setSelectedTenant(null);
    setTenantSearch("");
    setTenantResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!config) {
      toast.error("You are not allowed to send invites");
      return;
    }
    if (!roleName) {
      toast.error(`Role "${config.targetRole}" was not found in the role list`);
      return;
    }

        if (isOwnerInvite && !selectedTenant) {
      toast.error("Search and select the tenant first");
      return;
    }

    const phone = form.phone.trim();
    if (!/^\+?[0-9]{10,15}$/.test(phone)) {
      toast.error("Enter a valid phone number (10 to 15 digits)");
      return;
    }

    let subdomain = "";
    if (config.needsSubdomain) {
      subdomain = normalizeSubdomain(form.subdomain);
      if (!subdomain) {
        toast.error("Subdomain is required for Admin invite");
        return;
      }
      if (!isValidSubdomain(subdomain)) {
        toast.error(
          "Enter a valid subdomain (letters, numbers, hyphens; e.g. green-valley)"
        );
        return;
      }
      if (["www", "api", "admin", "app"].includes(subdomain)) {
        toast.error("That subdomain is reserved. Choose another.");
        return;
      }
    }

    let monthlyMaintenanceAmount;
    if (config.needsMaintenance) {
      const raw = String(form.monthlyMaintenanceAmount ?? "").trim();
      const amount = Number(raw);
      if (!raw || Number.isNaN(amount) || amount < 0) {
        toast.error("Enter a valid monthly maintenance amount");
        return;
      }
      monthlyMaintenanceAmount = amount;
    }

    const payload = {
      action: config.action,
      userid: localStorage.getItem("userid"),
      name: form.name.trim(),
      email: form.email.trim(),
      phone,
      role: roleName,
      ...(config.needsSubdomain
        ? { subdomain, domain: `${subdomain}.${ROOT_DOMAIN}` }
        : {}),
      ...(config.needsMaintenance ? { monthlyMaintenanceAmount } : {}),
    };

    setSubmitting(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success") {
        toast.success(data?.message || "Invitation sent successfully");
        resetForm();
      } else {
        toast.error(data?.message || "Invitation failed");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong"
      );
    } finally {
      if (didIncrement) loaderDecrement();
      setSubmitting(false);
    }
  };

  if (!config) {
    return (
      <div className="employee-form">
        <div className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-body">
                You do not have permission to send invites.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="container-fluid">
          <div
            className="d-flex flex-wrap justify-content-between align-items-end"
            style={{ gap: 12 }}
          >
            <div style={{ minWidth: 260 }}>
              <h1 className="m-0 txt_lft">Invite</h1>
              <p style={{ margin: 0 }}>
                Invite a {config.tabLabel} to your Adukkumadi platform.
              </p>
            </div>

            <ul
              className="nav nav-tabs"
              role="tablist"
              style={{ borderBottom: "none", marginBottom: 0 }}
            >
              <li className="nav-item">
                <button
                  type="button"
                  className="nav-link active"
                  role="tab"
                  aria-selected="true"
                >
                  {config.tabLabel}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {isOwnerInvite ? (
                  <div className="row invite-form-row">
                    <div className="col-12 col-md-6 col-lg-6">
                      <div className="form-group" style={{ position: "relative" }}>
                        <label className="form-label">
                          Search tenant (name or email) <span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Type tenant name or email"
                          value={tenantSearch}
                          onChange={(e) => setTenantSearch(e.target.value)}
                          disabled={!!selectedTenant}
                          autoComplete="off"
                        />
                        {!selectedTenant && tenantSearch.trim() ? (
                          <div className="list-group" style={{ position: "absolute", zIndex: 10, width: "100%" }}>
                            {searchingTenant ? (
                              <div className="list-group-item">Searching...</div>
                            ) : tenantResults.length ? (
                              tenantResults.map((t) => (
                                <button
                                  type="button"
                                  key={t.email}
                                  className="list-group-item list-group-item-action"
                                  onClick={() => pickTenant(t)}
                                >
                                  <strong>{t.name}</strong>
                                  <div style={{ fontSize: 12 }}>
                                    {t.email}{t.phone ? ` · ${t.phone}` : ""}
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="list-group-item text-muted">
                                No tenant found. The property must be approved and Let-out.
                              </div>
                            )}
                          </div>
                        ) : null}
                        {selectedTenant ? (
                          <button type="button" className="btn btn-link p-0 mt-1" onClick={clearTenant}>
                            Change tenant
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="row invite-form-row">
                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="form-group">
                      <label className="form-label">
                        Name <span className="required-star">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter name"
                        value={form.name}
                        onChange={onChange("name")}
                        readOnly={isOwnerInvite}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="form-group">
                      <label className="form-label">
                        Email <span className="required-star">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="Enter email"
                        value={form.email}
                        onChange={onChange("email")}
                        readOnly={isOwnerInvite}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-6 col-lg-3">
                    <div className="form-group">
                      <label className="form-label">
                        Phone <span className="required-star">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="Enter phone"
                        value={form.phone}
                        onChange={onChange("phone")}
                        readOnly={isOwnerInvite && !!form.phone}
                        required
                      />
                    </div>
                  </div>
                    <div className="col-12 col-md-6 col-lg-3">
                    <div className="form-group">
                      <label className="form-label">Role</label>
                      <input
                        type="text"
                        className="form-control"
                        value={loadingRoles ? "Loading..." : roleName}
                        readOnly
                        tabIndex={-1}
                        aria-readonly="true"
                      />
                    </div>
                  </div>
                </div>

                {config.needsMaintenance ? (
                  <div className="row invite-form-row">
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">
                          Monthly maintenance amount <span className="required-star">*</span>
                        </label>
                        <input
                          type="number"
                          className="form-control invite-amount-input"
                          placeholder="Enter amount"
                          min="0"
                          step="1"
                          inputMode="numeric"
                          value={form.monthlyMaintenanceAmount}
                          onChange={onChange("monthlyMaintenanceAmount")}
                          required
                        />
                      </div>
                    </div>
                  </div>
                ) : null}

                {config.needsSubdomain ? (
                  <div className="row invite-form-row">
                    <div className="col-12 col-md-6 col-lg-4">
                      <div className="form-group">
                        <label className="form-label">
                          Subdomain <span className="required-star">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. green-valley"
                            value={form.subdomain}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                subdomain: normalizeSubdomain(e.target.value),
                              }))
                            }
                            required
                            autoComplete="off"
                            spellCheck={false}
                          />
                          <div className="input-group-append">
                            <span className="input-group-text">
                              .{ROOT_DOMAIN}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="row invite-form-row">
                  <div className="col-12">
                    <div className="form-group invite-form-actions mb-0">
                      <div
                        className="d-flex flex-wrap justify-content-lg-end"
                        style={{ gap: 10 }}
                      >
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={submitting || loadingRoles || !roleName}
                        >
                          {submitting ? "Sending..." : config.submitLabel}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={resetForm}
                          disabled={submitting}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invite;